// GTM-Only Analytics Service
// This service uses only Google Tag Manager's dataLayer for all analytics and marketing tracking
// All events are sent to GTM, which then routes them to GA4, Meta Pixel, TikTok Pixel, etc.
//
// Anonymous auth events (routed via GTM to GA4/Meta):
// - anon_sign_in — guest started anonymous session
// - anon_account_linked — guest linked Google/Apple (same UID)
// - anon_session_abandoned — guest chose existing account (data lost)
// - existing_account_conflict_shown / existing_account_sign_in_chosen / existing_account_conflict_dismissed
// - cloud_onboarding_choice — post-link storage picker (provider + context)
//
// Welcome onboarding (routed via GTM to GA4/Meta):
// - onboarding_viewed — new user opened /welcome (layout_type: mobile|desktop)
// - onboarding_skipped — dismissed via Skip (close event for Meta)
// - onboarding_demo_clicked — Open example track CTA
// - onboarding_create_clicked — Create my first track CTA

import { getStoredUtm } from '../utils/utm';
import { getMetaCookies } from '../utils/metaCookies';

let analyticsEnabled = false;
let gtmLoaded = false;
let analyticsInitLogged = false;

// GTM Container ID - read from environment (optional for local development)
const GTM_CONTAINER_ID = import.meta.env.VITE_GTM_CONTAINER_ID || '';

// Check if running in local development
function isLocalDevelopment(): boolean {
  return import.meta.env.VITE_ENV === 'LOCAL';
}

// Validate GTM Container ID only when needed (not in local dev)
function validateGTMContainerId(): void {
  if (isLocalDevelopment()) {
    return; // Skip validation in local development
  }
  
  if (!GTM_CONTAINER_ID || GTM_CONTAINER_ID.trim() === '') {
    console.warn('[Analytics] VITE_GTM_CONTAINER_ID is not set. Analytics will be disabled.');
  }
}

// Initialize dataLayer if it doesn't exist
function initDataLayer() {
  if (typeof window === 'undefined') return;
  (window as any).dataLayer = (window as any).dataLayer || [];
}

// Initialize GTM
function initGTM(containerId: string) {
  try {
    if (typeof window === 'undefined' || gtmLoaded) return;
    
    initDataLayer();
    
    // Push GTM initialization event
    (window as any).dataLayer.push({ 
      'gtm.start': Date.now(), 
      event: 'gtm.js' 
    });
    
    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    document.head.appendChild(script);
    
    gtmLoaded = true;
  } catch (error) {
    console.error('Failed to initialize GTM:', error);
  }
}

// Parse UTM parameters from URL
function parseUtmFromUrl(): Record<string, string> | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    let found = false;

    for (const key of keys) {
      const value = params.get(key);
      if (value) {
        utm[key] = value;
        found = true;
      }
    }

    return found ? utm : null;
  } catch {
    return null;
  }
}

function storedUtmParams(): Record<string, string> {
  const utm = getStoredUtm();
  if (!utm) {
    return parseUtmFromUrl() || {};
  }
  const out: Record<string, string> = {};
  if (utm.utm_source) out.utm_source = utm.utm_source;
  if (utm.utm_medium) out.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) out.utm_campaign = utm.utm_campaign;
  if (utm.utm_term) out.utm_term = utm.utm_term;
  if (utm.utm_content) out.utm_content = utm.utm_content;
  if (utm.firstSeenAt) out.first_seen_at = utm.firstSeenAt;
  if (utm.landingPath) out.landing_page = utm.landingPath;
  if (utm.initialDesign) {
    out.design_variant = utm.initialDesign;
    out.initial_design = utm.initialDesign;
  }
  return out;
}

// Initialize analytics (GTM only)
export async function initAnalytics(consentAccepted: boolean) {
  analyticsEnabled = consentAccepted;
  
  if (!consentAccepted) {
    return;
  }
  
  // Skip analytics in local development
  if (isLocalDevelopment()) {
    if (!analyticsInitLogged) {
      analyticsInitLogged = true;
      console.log('[Analytics] Skipped in local development environment');
    }
    return;
  }
  
  // Validate GTM Container ID
  validateGTMContainerId();
  
  // If no GTM Container ID, skip initialization
  if (!GTM_CONTAINER_ID || GTM_CONTAINER_ID.trim() === '') {
    console.warn('[Analytics] GTM Container ID not configured. Analytics disabled.');
    return;
  }
  
  // Initialize dataLayer
  initDataLayer();
  
  // Store GTM container ID in localStorage for static HTML pages
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('gtm_container_id', GTM_CONTAINER_ID);
    }
  } catch (e) {
    // localStorage not available, continue without storing
  }
  
  // Load GTM
  initGTM(GTM_CONTAINER_ID);
  
  // Capture UTM parameters
  const utmParams = parseUtmFromUrl();
  
  // Set user properties via dataLayer
  const userProperties = {
    site_url: window.location.host,
    pwa_display_mode: (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator as any).standalone === true ? 'standalone' : 'browser',
    layout_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
    ...(utmParams || {})
  };
  
  // Push user properties to dataLayer
  (window as any).dataLayer.push({
    event: 'user_properties',
    ...userProperties
  });
}

// Disable analytics
export function disableAnalytics() {
  analyticsEnabled = false;
}

/** Read GDPR consent cookie used by CookieConsent / initAnalytics. */
export function hasAnalyticsConsent(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const consentCookie = document.cookie
      .split(';')
      .map((v) => v.trim())
      .find((v) => v.startsWith('cassette_gdpr_consent='));
    return consentCookie?.split('=')[1] === 'true';
  } catch {
    return false;
  }
}

// Set user ID
export function setUserId(userId: string) {
  if (!analyticsEnabled || isLocalDevelopment()) return;
  
  initDataLayer();
  (window as any).dataLayer.push({
    event: 'user_id_set',
    user_id: userId
  });
}

// Set user properties
export function setUserProperties(props: Record<string, unknown>) {
  if (!analyticsEnabled || isLocalDevelopment()) return;
  
  initDataLayer();
  (window as any).dataLayer.push({
    event: 'user_properties',
    ...props
  });
}

// Log custom event
export function logEvent(eventName: string, params?: Record<string, unknown>) {
  if (!analyticsEnabled || isLocalDevelopment()) return;
  
  initDataLayer();

  let utmParams: Record<string, string> = {};
  try {
    utmParams = storedUtmParams();
  } catch {
    utmParams = parseUtmFromUrl() || {};
  }

  const metaCookies = getMetaCookies();

  // Prepare event data
  const eventData = {
    event: eventName,
    site_url: window.location.host,
    page_title: document.title,
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_path: window.location.pathname,
    ...utmParams,
    ...metaCookies,
    ...(params || {}),
  };
  
  // Push to dataLayer
  (window as any).dataLayer.push(eventData);
}

// Track page view
export function pageView(path: string, title?: string, eventId?: string) {
  if (!analyticsEnabled || isLocalDevelopment()) return;
  
  initDataLayer();

  const pageData = {
    event: 'page_view',
    page_title: title || document.title,
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    ...storedUtmParams(),
    ...getMetaCookies(),
    ...(eventId ? { event_id: eventId } : {}),
  };
  
  (window as any).dataLayer.push(pageData);
}

// Simple unified analytics interface for easy importing
export const ga = {
  init: initAnalytics,
  disable: disableAnalytics,
  setUserId,
  setUserProperties,
  event: logEvent,
  pageView,
};

export default ga;